import { useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Badge } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { useActionExecution } from '../../hooks/useActionExecution';
import InlineQuoteNote from './InlineQuoteNote';
import { useAuth } from '../../../hooks/useAuth';
import { mapPersonaToUserRole } from '../../utils/role-mapping';
import { buildQuoteReviewPath } from '../sources/SourcePreviewHandler';
import { getRegionByProduct } from '../../../../i18n/utils';
import { deriveCurrencyFormat } from '../../../../i18n/currencies';
import type { ProductType } from '../../../../types';
import type {
  FormQuoteDraftAction,
  AssistantMessage,
  QuoteDraft,
  QuoteCopyResult,
} from '../../types';
import styles from './ActionCard.module.css';

interface Props {
  action: FormQuoteDraftAction;
  message: AssistantMessage;
}

/**
 * Format a raw numeric string with the product's currency symbol + grouping
 * separators (US `$`, UK `£`, AU `A$`). Limits/deductibles show no cents;
 * premiums show cents. Empty values pass through so caller fallbacks (`|| 'n/a'`)
 * still apply.
 */
function formatMoney(
  product: string,
  value?: string,
  withCents = false
): string {
  if (value == null || value === '') return value ?? '';
  // The value may arrive raw ("1000000") or already currency-formatted from the
  // server ("$1,000,000"). Strip any symbols/separators and re-parse so we format
  // exactly once, product-aware. Non-numeric input yields '' so caller fallbacks
  // (e.g. `|| 'n/a'`) apply instead of rendering "$NaN".
  const cleaned = String(value).replace(/[^0-9.-]/g, '');
  const numeric = Number(cleaned);
  if (cleaned === '' || Number.isNaN(numeric)) return '';
  const region = getRegionByProduct((product || 'PRIME_ONE_US') as ProductType);
  return deriveCurrencyFormat(
    region,
    numeric,
    withCents
      ? undefined
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 }
  );
}

/**
 * Build the header title, omitting segments the server left empty so we never
 * render a dangling separator or empty parens (e.g. "Quote options — ()").
 */
function buildOptionsTitle(
  sourceQuoteNumber?: string,
  accountName?: string
): string {
  const parts = ['Quote options'];
  if (sourceQuoteNumber) parts.push(`— ${sourceQuoteNumber}`);
  if (accountName) parts.push(`(${accountName})`);
  return parts.join(' ');
}

// Per-row UI state, keyed by draftId, tracking the Generate (persist) action.
interface RowState {
  generating?: boolean;
  result?: QuoteCopyResult;
  error?: string;
}

/**
 * Copy/re-rate quote-options card (OMNI-460). Renders the source quote header plus
 * one row per previewed option: OK rows show the re-rated premium and a Generate
 * button that persists the copy (`generate-quote-copy`); ERROR rows explain why the
 * option is not available. A generated row shows the new quote and a UW-review link.
 */
export default function QuoteDraftCard({ action, message }: Props) {
  // Hydrate from any persisted per-option outcome so a reloaded conversation shows an
  // already-generated option (new quote + UW-review link) instead of re-offering
  // Generate. The server sets draft.result once an option is generated (OMNI-460).
  const [rows, setRows] = useState<Record<string, RowState>>(() => {
    const hydrated: Record<string, RowState> = {};
    for (const d of action.drafts) {
      if (d.result?.newQuoteNumber) hydrated[d.draftId] = { result: d.result };
    }
    return hydrated;
  });
  const { mutateAsync } = useActionExecution();
  // Synchronous in-flight guard, keyed by draftId. The Generate button is
  // disabled via `loading` once `row.generating` renders, but React applies
  // that state asynchronously — a rapid double-click can fire a second
  // mutation before the re-render. A ref is read+set synchronously so only the
  // first click starts a `generate-quote-copy` request (avoids duplicate quotes).
  const inFlight = useRef<Set<string>>(new Set());
  const history = useHistory();
  const { accountType } = useAuth();
  const userRole = mapPersonaToUserRole(accountType);

  // Every row carries the same source-quote fields (set server-side from the header).
  const header = action.drafts[0];

  const handleGenerate = useCallback(
    async (draft: QuoteDraft) => {
      // Ignore repeat clicks while this option's request is already in flight.
      if (inFlight.current.has(draft.draftId)) return;
      inFlight.current.add(draft.draftId);
      setRows((prev) => ({ ...prev, [draft.draftId]: { generating: true } }));
      try {
        const res = await mutateAsync({
          correlationId: message.correlationId,
          messageId: message.messageId,
          action: 'generate-quote-copy',
          payload: (draft.generatePayload ?? {}) as Record<string, unknown>,
        });
        if (res?.success && res.result?.newQuoteNumber) {
          setRows((prev) => ({
            ...prev,
            [draft.draftId]: {
              result: {
                newQuoteId: res.result.newQuoteId ?? '',
                newQuoteNumber: res.result.newQuoteNumber ?? '',
                premium: res.result.premium ?? '',
                product: res.result.product ?? draft.product,
                noteDraft: res.result.noteDraft,
              },
            },
          }));
        } else {
          setRows((prev) => ({
            ...prev,
            [draft.draftId]: {
              error: res?.result?.message || 'Could not create the quote.',
            },
          }));
        }
      } catch {
        setRows((prev) => ({
          ...prev,
          [draft.draftId]: { error: 'Could not create the quote.' },
        }));
      } finally {
        // Clear the guard so a failed attempt can be retried. On success the
        // Generate button is gone (replaced by the confirmed card), so this
        // only re-enables retry after an error.
        inFlight.current.delete(draft.draftId);
      }
    },
    [mutateAsync, message]
  );

  return (
    <>
      {header && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            {buildOptionsTitle(header.sourceQuoteNumber, header.accountName)}
          </div>
          {(header.sourceLimit || header.sourcePremium) && (
            <div className={styles.cardSubtitle}>
              {`Current: limit ${
                formatMoney(header.product, header.sourceLimit) || 'n/a'
              }, Total Premium ${
                formatMoney(header.product, header.sourcePremium, true) || 'n/a'
              }`}
            </div>
          )}
        </div>
      )}

      {action.drafts.map((draft) => {
        const row = rows[draft.draftId] || {};

        // Generated (persisted) — show the new quote + UW-review link.
        if (row.result) {
          const uwPath = buildQuoteReviewPath(
            row.result.newQuoteId,
            row.result.product,
            userRole
          );
          const premium = formatMoney(
            row.result.product,
            row.result.premium,
            true
          );
          return (
            <div key={draft.draftId} className={styles.confirmed}>
              <div className={styles.confirmedHeader}>
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  className={styles.confirmedIcon}
                />
                <span className={styles.confirmedLabel}>Quote created</span>
              </div>
              <div className={styles.draftMetrics}>
                <span className={styles.draftMetricLabel}>Quote #</span>
                <span className={styles.confirmedValue}>
                  {row.result.newQuoteNumber}
                </span>
                {premium && (
                  <>
                    <span className={styles.draftMetricLabel}>
                      Total Premium
                    </span>
                    <span className={styles.confirmedValue}>{premium}</span>
                  </>
                )}
              </div>
              <Button
                size="xs"
                variant="outline"
                onClick={() => history.push(uwPath)}
              >
                Open UW review
              </Button>
              {/* OMNI-552: evaluate the generated quote and offer a pre-filled
                  Quote Note documenting the applied rules for confirm-and-add. */}
              {row.result.noteDraft && (
                <InlineQuoteNote
                  note={row.result.noteDraft}
                  message={message}
                />
              )}
            </div>
          );
        }

        // Preview failed — explain why; no Generate button.
        if (draft.status === 'ERROR') {
          return (
            <div key={draft.draftId} className={styles.card}>
              <div className={styles.cardTitle}>
                {draft.requestedLimit
                  ? `Limit ${formatMoney(draft.product, draft.requestedLimit)}`
                  : draft.product}
                <Badge size="xs" color="red" ml="xs">
                  NOT AVAILABLE
                </Badge>
              </div>
              <div className={styles.cardSubtitle}>
                {draft.errorReason || 'This option is not available.'}
              </div>
            </div>
          );
        }

        // OK preview — applied premium + Generate.
        return (
          <div key={draft.draftId} className={styles.card}>
            <div className={styles.cardTitle}>{draft.product}</div>
            <div className={styles.draftMetrics}>
              <span className={styles.draftMetricLabel}>Limit</span>
              <span className={styles.draftMetricValue}>
                {formatMoney(draft.product, draft.appliedLimit || draft.limit)}
              </span>
              <span className={styles.draftMetricLabel}>
                {draft.deductibleLabel || 'Deductible'}
              </span>
              <span className={styles.draftMetricValue}>
                {formatMoney(
                  draft.product,
                  draft.appliedDeductible || draft.deductible
                )}
              </span>
              <span className={styles.draftMetricLabel}>Total Premium</span>
              <span className={styles.draftMetricValue}>
                {formatMoney(draft.product, draft.totalPremium, true)}
              </span>
            </div>

            {(draft.adjustments ?? []).map((adj, idx) => (
              <div key={idx} className={styles.changeRow}>
                <span>{adj}</span>
              </div>
            ))}

            {row.error && (
              <div className={styles.cardSubtitle}>{row.error}</div>
            )}

            <div className={styles.buttonGroup}>
              <Button
                size="xs"
                color="teal"
                loading={row.generating}
                onClick={() => handleGenerate(draft)}
              >
                Generate
              </Button>
            </div>
          </div>
        );
      })}
    </>
  );
}
